import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchCourses,
  setSelectedCourseId,
  selectCourses,
  selectSelectedCourseId,
  selectCoursesLoading,
  selectCoursesError
} from './CoursesStore';
import { selectIsAuthenticated } from './AuthStore';
import LoadingScreen from '../common/LoadingScreen';
import ErrorScreen from '../common/ErrorScreen';

/**
 * CoursesProvider bootstraps courses globally before rendering children.
 * - Only fetches courses if user is authenticated (uses AuthProvider state).
 * - Shows a loading screen while fetching courses.
 * - On failure, retries up to 2 times, then shows error screen with manual retry option.
 * - Falls back to env `REACT_APP_COURSE_CURRENT` when fetch fails.
 * - Ensures `localStorage.course_id` is always set.
 */
const CoursesProvider = ({ children }) => {
  const dispatch = useDispatch();
  const courses = useSelector(selectCourses);
  const selectedCourseId = useSelector(selectSelectedCourseId);
  const isLoading = useSelector(selectCoursesLoading);
  const error = useSelector(selectCoursesError);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [retryCount, setRetryCount] = useState(0);
  const [hasExhaustedRetries, setHasExhaustedRetries] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return; // Skip if not authenticated
    dispatch(fetchCourses());
  }, [dispatch, isAuthenticated]);

  // Retry mechanism
  useEffect(() => {
    if (error && retryCount < 2 && isAuthenticated) {
      const timer = setTimeout(
        () => {
          setRetryCount((prev) => prev + 1);
          dispatch(fetchCourses());
        },
        1000 * (retryCount + 1)
      ); // 1s, 2s delays

      return () => clearTimeout(timer);
    } else if (error && retryCount >= 2) {
      // Mark as exhausted retries
      setHasExhaustedRetries(true);
    }
  }, [error, retryCount, dispatch, isAuthenticated]);

  // Ensure we always have a selected course id
  useEffect(() => {
    if (!isLoading) {
      const storedId = window.localStorage.getItem('course_id');
      const storedIdNum = storedId ? parseInt(storedId) || null : null;
      if (courses && courses.length > 0) {
        const id = storedIdNum || selectedCourseId || courses[0]?.id;
        if (id) dispatch(setSelectedCourseId(id));
      } else if (error) {
        // Fallback to env if fetch failed or empty
        const fallback = process.env.REACT_APP_COURSE_CURRENT;
        if (fallback) {
          const fallbackNum = parseInt(fallback) || null;
          if (fallbackNum) dispatch(setSelectedCourseId(fallbackNum));
        }
      }
    }
  }, [isLoading, courses, error, selectedCourseId, dispatch]);

  const shouldShowLoading = useMemo(() => {
    // Don't show loading if not authenticated
    if (!isAuthenticated) return false;

    // Show loading until we either have courses or have set a fallback course id
    const hasCourses = Array.isArray(courses) && courses.length > 0;
    const storedId = window.localStorage.getItem('course_id');
    const hasSelected = !!(
      (storedId && parseInt(storedId)) ||
      selectedCourseId
    );
    if (isLoading && !hasCourses && !hasSelected) return true;
    return false;
  }, [isLoading, courses, selectedCourseId, isAuthenticated]);

  // Handle manual retry
  const handleRetry = () => {
    setRetryCount(0);
    setHasExhaustedRetries(false);
    dispatch(fetchCourses());
  };

  // Show error screen if we have exhausted all retries
  if (hasExhaustedRetries && error && isAuthenticated) {
    const errorMessage = error.includes('500')
      ? 'Lỗi máy chủ nội bộ. Vui lòng thử lại sau.'
      : error.includes('400')
        ? 'Yêu cầu không hợp lệ. Vui lòng thử lại.'
        : error.includes('Network Error') || error.includes('fetch')
          ? 'Lỗi kết nối mạng. Vui lòng kiểm tra kết nối internet.'
          : `Không thể tải danh sách năm học: ${error}`;

    return <ErrorScreen message={errorMessage} onRetry={handleRetry} />;
  }

  if (shouldShowLoading) return <LoadingScreen />;

  return children;
};

export default CoursesProvider;
